/**
 * CandidatePool — Full listing with category sidebar, pagination, advanced filters, bulk upload.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Users, Plus, Search, X, CheckCircle2, Loader2, Star, ChevronDown, Upload, Download, Filter, ChevronLeft, ChevronRight, Edit2, Pencil, Send } from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDel, apiDownload, apiUpload } from '../../../lib/api';
import { toast } from 'sonner';

const EMPTY = {
  name: '', email: '', phone: '', current_role: '', current_employer: '',
  years_experience: '', skills: '', education: '', location: '',
  current_ctc: '', expected_ctc: '', notice_period_days: '',
  notes: '', category: 'General', source: 'Manual',
};

export default function CandidatePool() {
  const [candidates, setCandidates] = useState([]);
  const [catCounts, setCatCounts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const PAGE_SIZE = 20;

  // Filters
  const [search, setSearch] = useState('');
  const [selectedCat, setSelectedCat] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [adv, setAdv] = useState({ min_exp: '', max_exp: '', role: '', location: '', source: '' });

  // Forms
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [lastMatches, setLastMatches] = useState(null);

  // Bulk upload
  const [showBulk, setShowBulk] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const bulkRef = useRef(null);

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, page_size: PAGE_SIZE };
      if (search.trim()) params.q = search.trim();
      if (selectedCat) params.category = selectedCat;
      if (adv.min_exp) params.min_experience = adv.min_exp;
      if (adv.max_exp) params.max_experience = adv.max_exp;
      if (adv.role) params.current_role = adv.role;
      if (adv.location) params.location = adv.location;
      if (adv.source) params.source = adv.source;

      const r = await apiGet('/hr/recruitment/pool', params);
      setCandidates(r.items || []);
      setCatCounts(r.category_counts || []);
      setTotalCount(r.total || 0);
      setPages(r.pages || 1);
      setPage(pg);
    } catch { toast.error('Failed to load candidates'); }
    finally { setLoading(false); }
  }, [search, selectedCat, adv]);

  useEffect(() => { load(1); }, [load]);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      if (editingId) {
        await apiPut(`/hr/recruitment/pool/${editingId}`, form);
        toast.success('Candidate updated');
        setShowForm(false); setEditingId(null); setForm(EMPTY); load(page);
      } else {
        const r = await apiPost('/hr/recruitment/pool', form);
        toast.success(`${r.candidate.name} added to pool`);
        if (r.instant_matches?.length) setLastMatches(r.instant_matches);
        setShowForm(false); setForm(EMPTY); load(1);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      if (e?.response?.status === 409) {
        toast.error(detail || 'This candidate already exists in the pool (duplicate phone/email).', { duration: 6000 });
      } else {
        toast.error(detail || 'Save failed');
      }
    }
    finally { setSaving(false); }
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || '', email: c.email || '', phone: c.phone || '',
      current_role: c.current_role || '', current_employer: c.current_employer || '',
      years_experience: c.years_experience || '', skills: Array.isArray(c.skills) ? c.skills.join(', ') : (c.skills || ''),
      education: c.education || '', location: c.location || '',
      current_ctc: c.current_ctc || '', expected_ctc: c.expected_ctc || '',
      notice_period_days: c.notice_period_days || '',
      notes: c.notes || '', category: c.category || 'General', source: c.source || 'Manual',
    });
    setShowForm(true);
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name} from pool?`)) return;
    try { await apiDel(`/hr/recruitment/pool/${id}`); toast.success('Removed'); load(page); }
    catch { toast.error('Remove failed'); }
  };

  const downloadTemplate = async () => {
    try {
      await apiDownload('/hr/recruitment/pool/template', 'candidate-pool-template.xlsx');
    } catch { toast.error('Template download failed'); }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const data = await apiUpload('/hr/recruitment/pool/bulk-upload', fd);
      setBulkResult(data);
      if (data.skipped > 0) {
        toast(`✅ ${data.added} added. ⚠️ ${data.skipped} skipped (duplicates or missing name).`, { duration: 8000 });
      } else {
        toast.success(`${data.added} candidate(s) imported`);
      }
      load(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setBulkUploading(false); }
  };

  const allCats = [{ category: 'All Candidates', count: totalCount }, ...catCounts];

  // Change 3 — Excel export
  const downloadExcel = async () => {
    try {
      const tok = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      if (search.trim()) params.set('q', search.trim());
      if (selectedCat) params.set('category', selectedCat);
      if (adv.min_exp) params.set('min_experience', adv.min_exp);
      if (adv.max_exp) params.set('max_experience', adv.max_exp);
      if (adv.role) params.set('current_role', adv.role);
      if (adv.location) params.set('location', adv.location);
      if (adv.source) params.set('source', adv.source);
      const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
      const r = await fetch(`${BACKEND}/api/hr/recruitment/pool/export.xlsx?${params.toString()}`, {
        headers: { Authorization: `Bearer ${tok}` }, credentials: 'include',
      });
      if (!r.ok) throw new Error('Export failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `candidates-${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${totalCount} candidates`);
    } catch { toast.error('Export failed'); }
  };

  // Change 4 — Push to Job Opening
  const [pushCandidate, setPushCandidate] = useState(null);
  const [jobOpenings, setJobOpenings] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [pushingJobId, setPushingJobId] = useState('');
  const [pushing, setPushing] = useState(false);
  const [jobSearch, setJobSearch] = useState('');

  const openPush = async (candidate) => {
    setPushCandidate(candidate); setJobSearch(''); setPushingJobId(''); setLoadingJobs(true);
    try {
      const r = await apiGet('/hr/jobs', { status: 'open', limit: 100 });
      setJobOpenings(r?.items || r || []);
    } catch { toast.error('Could not load job openings'); }
    setLoadingJobs(false);
  };

  const pushToJob = async () => {
    if (!pushingJobId) return toast.error('Select a job opening');
    setPushing(true);
    try {
      const tok = localStorage.getItem('access_token');
      const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
      const fd = new FormData();
      fd.append('name', pushCandidate.name || '');
      if (pushCandidate.email) fd.append('email', pushCandidate.email);
      if (pushCandidate.phone) fd.append('phone', pushCandidate.phone);
      fd.append('source', 'candidate_pool');
      if (pushCandidate.current_employer) fd.append('current_company', pushCandidate.current_employer);
      if (pushCandidate.current_ctc) fd.append('current_ctc', String(pushCandidate.current_ctc));
      if (pushCandidate.expected_ctc) fd.append('expected_ctc', String(pushCandidate.expected_ctc));
      if (pushCandidate.notice_period_days) fd.append('notice_period_days', String(pushCandidate.notice_period_days));
      await fetch(`${BACKEND}/api/hr/jobs/${pushingJobId}/candidates`, {
        method: 'POST', body: fd,
        headers: { Authorization: `Bearer ${tok}` }, credentials: 'include',
      });
      const job = jobOpenings.find(j => j.id === pushingJobId);
      toast.success(`${pushCandidate.name} pushed to "${job?.title || 'Job Opening'}" → Applied stage`);
      setPushCandidate(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Push failed'); }
    setPushing(false);
  };

  return (
    <div className="flex gap-0 min-h-screen" data-testid="candidate-pool-page">
      {/* Left sidebar — categories */}
      <aside className="w-52 shrink-0 border-r border-slate-200 bg-white p-3 space-y-1">
        <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-2">Categories</div>
        {allCats.map((c) => {
          const active = c.category === 'All Candidates' ? !selectedCat : selectedCat === c.category;
          return (
            <button
              key={c.category}
              onClick={() => { setSelectedCat(c.category === 'All Candidates' ? '' : c.category); setPage(1); }}
              className={`w-full text-left px-2.5 py-2 rounded-sm text-xs flex items-center justify-between gap-1 ${active ? 'bg-indigo-50 text-[#3b4872] font-bold border border-[#3b4872]/20' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid={`cat-filter-${c.category.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span className="truncate">{c.category}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-[#3b4872] text-white' : 'bg-slate-100 text-slate-500'}`}>{c.count}</span>
            </button>
          );
        })}
        <div className="pt-3 mt-3 border-t border-slate-100">
          <button onClick={downloadTemplate} className="w-full text-left text-xs text-[#3b4872] hover:underline flex items-center gap-1.5 px-2" data-testid="cp-download-template">
            <Download size={12} /> Download Template
          </button>
        </div>
      </aside>

      {/* Right content */}
      <div className="flex-1 min-w-0 p-5 space-y-4">
        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, role, skills…"
              className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#3b4872]"
              data-testid="cp-search"
            />
          </div>
          <button onClick={() => setShowAdvanced((v) => !v)} className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs flex items-center gap-1 hover:bg-slate-50" data-testid="cp-advanced-toggle">
            <Filter size={12} /> Filters {showAdvanced ? <ChevronDown size={10} className="rotate-180" /> : <ChevronDown size={10} />}
          </button>
          <button onClick={() => setShowForm(true)} className="px-3 py-1.5 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold flex items-center gap-1 hover:bg-[#2d3856]" data-testid="cp-add-btn">
            <Plus size={13} /> Add Candidate
          </button>
          <button
            onClick={downloadExcel}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 text-slate-700 rounded-sm text-xs font-display font-bold hover:bg-slate-50"
            data-testid="cp-export-btn"
            title={`Download ${totalCount} candidates as Excel`}
          >
            <Download size={13} /> Download ({totalCount})
          </button>
          <div className="relative">
            <input ref={bulkRef} type="file" accept=".xlsx,.xls,.xlsm" onChange={handleBulkUpload} className="hidden" />
            <button
              onClick={() => bulkRef.current?.click()}
              disabled={bulkUploading}
              className="px-3 py-1.5 border border-[#3b4872] text-[#3b4872] rounded-sm text-xs font-display font-bold flex items-center gap-1 hover:bg-indigo-50 disabled:opacity-40"
              data-testid="cp-bulk-upload-btn"
            >
              {bulkUploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Bulk Upload
            </button>
          </div>
        </div>

        {/* Advanced filters */}
        {showAdvanced && (
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs" data-testid="cp-advanced-filters">
            <div>
              <label className="text-slate-500 block mb-0.5">Min Exp (yrs)</label>
              <input type="number" min="0" value={adv.min_exp} onChange={(e) => setAdv(a => ({ ...a, min_exp: e.target.value }))} className="w-full border border-slate-300 rounded-sm px-2 py-1 text-xs" data-testid="cp-filter-min-exp" />
            </div>
            <div>
              <label className="text-slate-500 block mb-0.5">Max Exp (yrs)</label>
              <input type="number" min="0" value={adv.max_exp} onChange={(e) => setAdv(a => ({ ...a, max_exp: e.target.value }))} className="w-full border border-slate-300 rounded-sm px-2 py-1 text-xs" data-testid="cp-filter-max-exp" />
            </div>
            <div>
              <label className="text-slate-500 block mb-0.5">Current Role</label>
              <input value={adv.role} onChange={(e) => setAdv(a => ({ ...a, role: e.target.value }))} className="w-full border border-slate-300 rounded-sm px-2 py-1 text-xs" data-testid="cp-filter-role" />
            </div>
            <div>
              <label className="text-slate-500 block mb-0.5">Location</label>
              <input value={adv.location} onChange={(e) => setAdv(a => ({ ...a, location: e.target.value }))} className="w-full border border-slate-300 rounded-sm px-2 py-1 text-xs" data-testid="cp-filter-location" />
            </div>
            <div>
              <label className="text-slate-500 block mb-0.5">Source</label>
              <select value={adv.source} onChange={(e) => setAdv(a => ({ ...a, source: e.target.value }))} className="w-full border border-slate-300 rounded-sm px-2 py-1 text-xs bg-white" data-testid="cp-filter-source">
                <option value="">All</option>
                <option>Manual</option>
                <option>Resume Scan</option>
                <option>Excel Import</option>
              </select>
            </div>
          </div>
        )}

        {/* Bulk result */}
        {bulkResult && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs flex items-center justify-between">
            <span className="text-emerald-800 font-semibold">{bulkResult.added} candidate(s) imported · {bulkResult.skipped?.length || 0} skipped</span>
            <button onClick={() => setBulkResult(null)}><X size={14} className="text-emerald-600" /></button>
          </div>
        )}

        {/* Instant match result */}
        {lastMatches && (
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs">
            <div className="flex items-center justify-between mb-1">
              <div className="font-semibold text-blue-800">{lastMatches.length} instant job match(es)</div>
              <button onClick={() => setLastMatches(null)}><X size={12} className="text-blue-600" /></button>
            </div>
            {lastMatches.map((m) => <div key={m.job_id} className="text-blue-700">{m.role_title} — {m.match_score}% match</div>)}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="cp-table">
          <div className="overflow-x-auto">
          <table className="min-w-full text-sm whitespace-nowrap">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-display">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Current Role</th>
                <th className="text-left px-3 py-2">Employer</th>
                <th className="text-right px-3 py-2">Exp</th>
                <th className="text-left px-3 py-2">Location</th>
                <th className="text-left px-3 py-2">Skills</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-right px-3 py-2">Curr. Salary</th>
                <th className="text-right px-3 py-2">Exp. Salary</th>
                <th className="text-right px-3 py-2">Notice (days)</th>
                <th className="text-center px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12} className="text-center py-10 text-slate-400"><Loader2 size={16} className="animate-spin inline mr-2" />Loading…</td></tr>
              )}
              {!loading && candidates.length === 0 && (
                <tr><td colSpan={12} className="text-center py-10 text-slate-400 italic">No candidates match the filters.</td></tr>
              )}
              {!loading && candidates.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`cp-row-${c.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-display font-semibold">{c.name}</div>
                    <div className="text-[10px] text-slate-400">{c.phone || '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">{c.current_role || '—'}</td>
                  <td className="px-3 py-2 text-xs truncate max-w-[120px]">{c.current_employer || '—'}</td>
                  <td className="px-3 py-2 text-xs text-right">{c.years_experience ? `${c.years_experience}y` : '—'}</td>
                  <td className="px-3 py-2 text-xs">{c.location || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(() => {
                        const skills = Array.isArray(c.skills)
                          ? c.skills
                          : typeof c.skills === 'string'
                            ? c.skills.split(',').map(s => s.trim()).filter(Boolean)
                            : [];
                        return (
                          <>
                            {skills.slice(0, 3).map((s) => (
                              <span key={s} className="px-1.5 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 text-[10px] font-semibold">{s}</span>
                            ))}
                            {skills.length > 3 && <span className="text-[10px] text-slate-400">+{skills.length - 3}</span>}
                          </>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">{c.category || 'General'}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{c.source || 'Manual'}</td>
                  <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                    {c.current_ctc || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-right whitespace-nowrap font-semibold text-emerald-700">
                    {c.expected_ctc || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-right">{c.notice_period_days || '—'}</td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => openEdit(c)} className="text-slate-400 hover:text-[#3b4872] p-1" data-testid={`cp-edit-${c.id}`} title="Edit"><Edit2 size={14} /></button>
                      <button onClick={() => openPush(c)} className="text-emerald-500 hover:text-emerald-700 p-1 rounded-sm hover:bg-emerald-50" title="Push to job opening" data-testid={`cp-push-${c.id}`}><Send size={13} /></button>
                      <button onClick={() => remove(c.id, c.name)} className="text-rose-400 hover:text-rose-600 p-1" data-testid={`cp-remove-${c.id}`} title="Remove"><X size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>  {/* close overflow-x-auto */}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between text-xs" data-testid="cp-pagination">
            <span className="text-slate-500">{totalCount} candidates · Page {page} of {pages}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => load(page - 1)} disabled={page <= 1} className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30" data-testid="cp-prev"><ChevronLeft size={14} /></button>
              {Array.from({ length: Math.min(pages, 5) }, (_, i) => {
                const pg = page <= 3 ? i + 1 : page - 2 + i;
                if (pg < 1 || pg > pages) return null;
                return (
                  <button key={pg} onClick={() => load(pg)} className={`px-2.5 py-1 rounded border text-xs ${pg === page ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'border-slate-200 hover:bg-slate-50'}`}>{pg}</button>
                );
              })}
              <button onClick={() => load(page + 1)} disabled={page >= pages} className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30" data-testid="cp-next"><ChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Add Candidate Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-sm shadow-xl" data-testid="cp-add-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white">
              <h3 className="font-display font-bold">{editingId ? 'Edit Candidate' : 'Add Candidate'}</h3>
              <button onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY); }}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              {[['name', 'Full Name *'], ['email', 'Email'], ['phone', 'Phone'], ['current_role', 'Current Role'], ['current_employer', 'Current Employer'], ['education', 'Education'], ['location', 'Location'], ['current_ctc', 'Current Salary (₹)'], ['expected_ctc', 'Expected Salary (₹)']].map(([k, label]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
                  <input value={form[k]} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))} className="w-full border border-slate-300 rounded-sm px-3 py-1.5 text-sm" />
                </div>
              ))}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Notice Period (days)</label>
                <input type="text" min="0" value={form.notice_period_days} onChange={(e) => setForm(f => ({ ...f, notice_period_days: e.target.value }))} className="w-full border border-slate-300 rounded-sm px-3 py-1.5 text-sm" placeholder="e.g. 30 days, 2 months, Immediate, Negotiable" data-testid="cp-notice-period" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Years Experience</label>
                <input type="number" min="0" step="0.5" value={form.years_experience} onChange={(e) => setForm(f => ({ ...f, years_experience: e.target.value }))} className="w-full border border-slate-300 rounded-sm px-3 py-1.5 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Skills (comma-separated)</label>
                <input value={form.skills} onChange={(e) => setForm(f => ({ ...f, skills: e.target.value }))} className="w-full border border-slate-300 rounded-sm px-3 py-1.5 text-sm" placeholder="AutoCAD, Project Management, …" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Category</label>
                <input value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className="w-full border border-slate-300 rounded-sm px-3 py-1.5 text-sm" placeholder="Engineering, Finance, HR…" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Notes</label>
                <textarea rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-slate-300 rounded-sm px-3 py-1.5 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t">
              <button onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY); }} className="px-3 py-1.5 text-sm">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-1.5 bg-[#3b4872] text-white rounded-sm text-sm font-bold disabled:opacity-50" data-testid="cp-save-btn">
                {saving ? <Loader2 size={14} className="animate-spin inline" /> : <CheckCircle2 size={14} className="inline mr-1" />} {editingId ? 'Save Changes' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Push to Job Opening Modal */}
      {pushCandidate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPushCandidate(null)}>
          <div className="bg-white rounded-md w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()} data-testid="cp-push-modal">
            <div className="p-5 border-b flex items-center justify-between">
              <div>
                <h2 className="font-display font-bold text-base">Push to Job Opening</h2>
                <p className="text-xs text-slate-500 mt-0.5">{pushCandidate.name} will be added to the Applied stage.</p>
              </div>
              <button onClick={() => setPushCandidate(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="px-5 pt-4 pb-2">
              <div className="p-3 bg-slate-50 rounded border border-slate-200 text-xs flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#3b4872] text-white flex items-center justify-center font-bold text-sm shrink-0">{(pushCandidate.name || '?')[0].toUpperCase()}</div>
                <div>
                  <div className="font-display font-semibold text-sm">{pushCandidate.name}</div>
                  <div className="text-slate-500">{pushCandidate.current_role || '—'} · {pushCandidate.years_experience ? `${pushCandidate.years_experience}y exp` : '—'}</div>
                  {pushCandidate.expected_ctc && <div className="text-emerald-700 font-medium">Expected: {pushCandidate.expected_ctc}</div>}
                </div>
              </div>
            </div>
            <div className="px-5 pb-5">
              <label className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 block mb-2">Select Job Opening *</label>
              <input value={jobSearch} onChange={e => setJobSearch(e.target.value)} placeholder="Search job title, department…"
                className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm mb-2 focus:border-[#3b4872] focus:outline-none" data-testid="cp-push-job-search" autoFocus />
              {loadingJobs ? (
                <div className="text-center py-6 text-slate-400"><Loader2 size={16} className="animate-spin inline mr-2" />Loading openings…</div>
              ) : (
                <div className="border border-slate-200 rounded-sm max-h-56 overflow-y-auto divide-y divide-slate-100">
                  {jobOpenings.filter(j => !jobSearch || (j.title || '').toLowerCase().includes(jobSearch.toLowerCase()) || (j.department || '').toLowerCase().includes(jobSearch.toLowerCase())).map(job => (
                    <div key={job.id} onClick={() => setPushingJobId(job.id)}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${pushingJobId === job.id ? 'bg-indigo-50 border-l-2 border-[#3b4872]' : 'hover:bg-slate-50'}`}
                      data-testid={`cp-push-job-${job.id}`}>
                      <input type="radio" checked={pushingJobId === job.id} readOnly className="w-4 h-4 accent-[#3b4872] shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-display font-semibold truncate">{job.title}</div>
                        <div className="text-[10px] text-slate-400">{job.department || '—'} · {job.headcount || 1} opening(s){job.location ? ` · ${job.location}` : ''}</div>
                      </div>
                      <span className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold ${job.status === 'open' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{(job.status || '').toUpperCase()}</span>
                    </div>
                  ))}
                  {!loadingJobs && jobOpenings.filter(j => !jobSearch || (j.title || '').toLowerCase().includes(jobSearch.toLowerCase())).length === 0 && (
                    <div className="px-3 py-4 text-xs text-slate-400 italic text-center">No open job openings found.</div>
                  )}
                </div>
              )}
            </div>
            <div className="px-5 pb-5 flex justify-end gap-2 border-t pt-4">
              <button onClick={() => setPushCandidate(null)} className="px-4 py-2 border border-slate-300 rounded-sm text-sm font-display hover:bg-slate-50">Cancel</button>
              <button onClick={pushToJob} disabled={!pushingJobId || pushing}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-sm text-sm font-display font-bold hover:bg-emerald-700 disabled:opacity-40"
                data-testid="cp-push-confirm-btn">
                {pushing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {pushing ? 'Pushing…' : 'Push to Applied Stage'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
